import { arrow, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { autoUpdate } from '@pypestream/floating-ui-dom';
import { property, query, state } from 'lit/decorators.js';
import { nanoid } from 'nanoid';
import { signal, Signal } from '@lit-labs/preact-signals';
import { ClickAwayController, FocusTrapController } from '../../../internal';
import { BaseElement, PSCustomEvent } from '../../base-element';
import { MenuItemSelectedEvent, MenuItemWC } from '../../menu/src/menu-item.wc';
import {
  PortalDestinationWC,
  PortalReadyEvent,
} from '../../portal/portal-destination.wc';
import { PageWC } from '../../page';

export type PopoverAutoCloseEvent = PSCustomEvent<
  // eslint-disable-next-line no-use-before-define
  PopoverBaseWC,
  MouseEvent
>;

declare global {
  interface GlobalEventHandlersEventMap {
    'popover-autoclose': PopoverAutoCloseEvent;
  }
}

export const popoverItemsSignal: Signal<string[]> = signal<string[]>([]);

export class PopoverBaseWC extends BaseElement {
  onClickAway = new ClickAwayController({
    host: this,
  });

  focusTrapController = new FocusTrapController(
    this,
    true,
    () => this.preventScroll
  );

  /**
   * Resolved from the `trigger` id. `protected` so a subclass can interrogate
   * the trigger it opens against — `PopoverTooltipWC` re-checks it before
   * replaying a deferred show (PSTUDIO-4034).
   */
  protected triggerEl: Element | null;

  private parentEl: Element | null;

  private cleanup: ReturnType<typeof autoUpdate> | undefined;

  private mutationObserver: MutationObserver;

  @query('#popover-container') containerEl: HTMLElement;

  @query('#popover-arrow') arrowEl: HTMLElement;

  /**
   * The element id the popover will be anchored to
   */
  @property() trigger: string | Element;

  /**
   * Add event listeners to the trigger element.
   */
  @property({ type: Boolean, attribute: 'disable-trigger-listeners' })
  disableTriggerListeners: boolean = false;

  /**
   * The trigger action type which executes the popover action.
   */
  @property({ reflect: true, attribute: 'trigger-action' }) triggerAction:
    | 'click'
    | 'click-and-hover'
    | 'context' = 'click';

  /**
   * Activates the positioning logic and shows the popover. When this attribute is removed, the positioning logic is torn
   * down and the popup will be hidden.
   */
  @property({ type: Boolean, reflect: true }) active = false;

  @property({ reflect: false }) actionType:
    | 'hover'
    | 'click'
    | 'focus'
    | 'keyboard'
    | 'unknown' = 'unknown';

  /**
   * The preferred UI width
   */
  @property({ reflect: true }) width: 'default' | 'auto' = 'default';

  /**
   * The preferred placement of the popup. Note that the actual placement will vary as configured to keep the
   * panel inside of the viewport.
   */
  @property({ reflect: true }) placement:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'left'
    | 'left-start'
    | 'left-end' = 'top';

  /** Attaches an arrow to the popup. */
  @property({ type: Boolean }) hasArrow = false;

  /**
   * The amount of padding between the arrow and the edges of the popup. If the popup has a border-radius, for example,
   * this will prevent it from overflowing the corners.
   */
  @property({ attribute: 'arrow-padding', type: Number }) arrowPadding = 10;

  /** The distance in pixels from which to offset the panel away from its anchor. */
  @property({ type: Number }) distance = 0;

  /** The distance in pixels from which to offset the panel along its anchor. */
  @property({ type: Number }) skidding = 0;

  /**
   * When set, placement of the popup will flip to the opposite site to keep it in view.
   */
  @property({ type: Boolean }) flip = true;

  /** Moves the popup along the axis to keep it in view when clipped. */
  @property({ type: Boolean }) shift = true;

  /**
   * Hides popover on click outside
   */
  @property({ type: Boolean, reflect: true }) closeOnClickOutside = true;

  /**
   * Hides popover on esc btn click
   */
  @property({ type: Boolean, reflect: true }) closeOnEscClick = true;

  /**
   * Skips using the built-in portal and instead
   */
  @property({ type: Boolean, reflect: true, attribute: 'omit-portal' })
  omitPortal = false;

  @property({ type: Boolean, reflect: true, attribute: 'disable-focus-trap' })
  disableFocusTrap = false;

  /**
   * PSTUDIO-4033 — opt in to a focus trap that never scrolls the viewport.
   *
   * The trap hands focus back to its trigger ~500ms after the popover closes.
   * When the app deliberately scrolled somewhere else in the meantime (the
   * workflows table revealing a freshly inserted block, PSTUDIO-3848), that
   * late `focus()` makes the browser scroll the trigger back — centering it,
   * measured at 476px — and the reveal is undone.
   *
   * Scoped to the RETURN alone — the initial focus, the pull-back when focus
   * escapes and the Tab wrap at a group boundary all keep scrolling natively,
   * so focus can never end up alive but off-screen (WCAG 2.4.7). That scoping
   * is why `FocusTrapController` does not hand this to `focus-trap`'s own
   * `preventScroll` option, which would suppress every one of those moves; a
   * popover routinely outgrows its `max-height` and scrolls internally, so the
   * wrap in particular must stay visible.
   *
   * OFF by default: turn it on only where the consumer performs its own
   * deliberate scroll that the late return would otherwise undo.
   */
  @property({ type: Boolean, reflect: true, attribute: 'prevent-scroll' })
  preventScroll = false;

  /**
   * Enables / disables automatically closing a Popover when a nested menu item has been selected
   */
  @property({ type: Boolean, reflect: true }) stayOpenOnSelect = false;

  @property({ type: Boolean, reflect: false }) defaultActive = false;

  @property({ type: Boolean, reflect: false }) isFocused = false;

  @property({ reflect: false, attribute: false }) projectedContent = this;

  @property({ type: Boolean, reflect: true }) disabled: boolean = false;

  /**
   * Rendering performance improvement. When enabled, the Popover will immediately move to the projected portal as soon as possible (even when closed) + not move back to the original container when closed
   */
  @property({ type: Boolean, reflect: true, attribute: 'keep-mounted' })
  keepMounted = false;

  /** Computed position of poopver container */
  @state() transform: string;

  constructor() {
    super();
    this.onPortalReady = this.onPortalReady.bind(this);

    this.onClick = this.onClick.bind(this);
    this.onContextMenu = this.onContextMenu.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onMouseOut = this.onMouseOut.bind(this);
    this.onMouseOver = this.onMouseOver.bind(this);
  }

  async connectedCallback() {
    super.connectedCallback();

    if (this.parentElement && !this.parentEl) {
      // Keep the reference of parent element to append `this` instance of popover once portal is closed.
      this.parentEl = this.parentElement;
    }

    this.focusTrapController.hostConnected();
    this.handleKeyDown = this.handleKeyDown.bind(this);

    if (this.closeOnClickOutside) {
      this.onClickAway.excludeElement = this.triggerEl as HTMLElement;
    }

    document.addEventListener('portal-destination-ready', this.onPortalReady);
  }

  // eslint-disable-next-line class-methods-use-this
  onPortalReady(e: PortalReadyEvent) {
    if (e.detail.name && e.detail.name === this.popoverId) {
      // console.log(
      //   'this component portal is ready',
      //   e.detail.name,
      //   this.popoverId,
      //   this.keepMounted
      // );

      if (!this.omitPortal && this.keepMounted) {
        this.handleProjectContent(true);
      }
    }
  }

  /** Pending deferred close from {@link reposition} when the trigger is gone. */
  private noTriggerCloseTimer: ReturnType<typeof setTimeout> | undefined;

  /**
   * Pending focus-trap activation scheduled by {@link updated} when the popover
   * opens. Replaced when the open branch runs again, cleared on the
   * `true → false` transition of `active`, and skipped inside the callback when
   * the host is detached by the time it fires.
   *
   * Not cleared in `disconnectedCallback`, which also runs when an open popover
   * is moved into its portal destination. Limitation: a host skipped as
   * detached and attached again later while `active` is still true is not
   * re-armed, because nothing schedules an activation on connect.
   */
  private focusTrapActivationTimer: ReturnType<typeof setTimeout> | undefined;

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.noTriggerCloseTimer);
    this.noTriggerCloseTimer = undefined;
    this.focusTrapController?.hostDisconnected();
    this.stop();
    this.removeOpenListeners();
    this.mutationObserver?.disconnect();

    document.removeEventListener(
      'portal-destination-ready',
      this.onPortalReady
    );

    this.removeEventListener('click', this.onClick);
    this.removeEventListener('contextMenu', this.onContextMenu);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('mouseout', this.onMouseOut);
    this.removeEventListener('mouseover', this.onMouseOver);
  }

  firstUpdated() {
    this.onClickAway.element = this.containerEl;
    this.onClickAway.callback = (e) => this.handleClickOutside(e);

    const idAttribute = this.getAttribute('id');
    if (idAttribute && !this.popoverId) {
      this.popoverId = idAttribute;
    } else if (!this.popoverId) {
      this.popoverId = `popover-${nanoid()}`;
    }

    if (this.defaultActive) {
      this.show();
      this.isFocused = true;
    }

    // if (this.projectionTrigger === 'first-updated') {
    //   console.log('first-updated', this.popoverId);
    //   this.handleProjectContent(true);
    // }
  }

  /**
   * Every popover, keyed by the element it is anchored to.
   *
   * Written in {@link handleTriggerChange} beside the `data-popover-id` stamp.
   * A `WeakMap`, so a trigger that leaves the DOM takes its entry with it.
   *
   * Nothing ever deletes an entry, on purpose. A delete on disconnect would
   * fire at the worst possible moment — projecting into the portal moves the
   * host, so `disconnectedCallback` runs on a popover that is very much still
   * open — and a delete on re-anchoring would only be correct while no second
   * popover had claimed the same trigger. Every kind of staleness is settled at
   * the READ site instead, where the live instance can be interrogated: see the
   * two guards in {@link opensANestedPopover}.
   *
   * One entry per trigger: a second popover anchored to the same element
   * replaces the first. That is the same ambiguity the single `data-popover-id`
   * attribute already had, not a new one.
   */
  private static readonly popoverByTrigger = new WeakMap<
    Element,
    PopoverBaseWC
  >();

  /**
   * Is the selected item the trigger of a SUBMENU this click is operating?
   *
   * `ps-menu` emits `menu-item-selected` on ITSELF (its `handleClick` calls
   * `this.emit('menu-item-selected')`, `menu/src/menu.wc.ts`), so a submenu's
   * trigger item reports through the menu that CONTAINS it — the outer one —
   * and nothing in the event path distinguishes it from a plain row.
   *
   * What the item is read for is therefore the popover REGISTERED against it in
   * {@link popoverByTrigger}, not a marker attribute. The attribute pair this
   * replaced (`data-popover-id` + `data-popover-open`) was wrong on both sides:
   *
   * - the open marker is not exclusive to this class. Pro Studio writes it
   *   itself on the row's `<td>` (`draggable-row.tsx` sets it, `cell.tsx`
   *   removes it) and that `<td>` IS a resolved `triggerEl` carrying
   *   `data-popover-id` — `RowContextMenu` is handed `triggerId={cellId}`. The
   *   reason the pair never fired there is not the element being someone
   *   else's: it is `MenuWC.handleClick` (`menu/src/menu.wc.ts:21-29`), which
   *   computes `target.closest('ps-menu-item')` and emits NOTHING when there is
   *   no such item — so `detail.item` is always a `ps-menu-item`, and a `<td>`
   *   can never be handed to this method.
   * - `data-popover-open` is written only by {@link show} and cleared only by
   *   {@link hide}, so a popover the consumer drives with `active` as a prop
   *   (`row-context-menu.tsx`: `active={active}` + `disableTriggerListeners`)
   *   never had it at all, and the nested case simply was not covered for that
   *   shape.
   *
   * The registered popover answers both, and answers the collapse gesture too.
   * A click on a `click`-triggered submenu trigger reaches the trigger's own
   * listener ({@link onClick}) BEFORE the menu emits — the item is a descendant
   * of the menu — so on a second click the submenu is already closed again by
   * the time this runs. Asking "does this item drive a submenu" instead of "is
   * one open" is what keeps folding a submenu back up from collapsing the whole
   * dropdown.
   *
   * Two things are deliberately NOT treated as submenu navigation:
   *
   * - anything but `triggerAction === 'click'`. A `click-and-hover` popover is
   *   a tooltip (`PopoverTooltipWC`'s constructor sets exactly that), and
   *   resting the pointer on an item opens it — a click on that item is still
   *   an ordinary selection, and the menu must close.
   * - a `disableTriggerListeners` popover that is not currently `active`. Such
   *   a popover ignores the click entirely unless its consumer opens it, so the
   *   only evidence available here is that it IS open. LIMITATION, stated
   *   rather than papered over: this covers an app-controlled submenu that was
   *   already open (the collapse gesture and anything while it is up); a click
   *   that is the consumer's own cue to open one for the first time is not
   *   recognised, since at the moment this runs nothing distinguishes it from a
   *   plain selection.
   */
  // eslint-disable-next-line class-methods-use-this
  private opensANestedPopover(event: MenuItemSelectedEvent): boolean {
    const item = event.detail?.item as HTMLElement | undefined;
    const anchored = item && PopoverBaseWC.popoverByTrigger.get(item);

    // Staleness guards, standing in for the delete-on-disconnect the registry
    // cannot afford: a popover that has left the document, or that has since
    // been re-anchored elsewhere, is not this item's submenu.
    if (!anchored || !anchored.isConnected || anchored.triggerEl !== item) {
      return false;
    }

    if (anchored.triggerAction !== 'click') return false;

    return !anchored.disableTriggerListeners || anchored.active;
  }

  private handleItemSelected = (event: MenuItemSelectedEvent) => {
    event.stopPropagation();
    const target = event.target as MenuItemWC;

    // Hide the dropdown when a menu item is selected.
    //
    // Deliberately NOT gated on being top of `popoverItemsSignal`: a selection
    // has to close its own menu whatever else is stacked above it. Gating on
    // the stack left a popover open forever whenever anything else sat on top
    // — measured on a cell whose validation tooltip was also active: the
    // context menu stayed `active` with its node still parked in the portal
    // (PSTUDIO-4229). What replaces it is a test of the GESTURE rather than of
    // stack position — see {@link opensANestedPopover}.
    if (
      !this.stayOpenOnSelect &&
      target.tagName.toLowerCase().includes('ps-menu') &&
      !this.opensANestedPopover(event)
    ) {
      this.hide(event, 'handleItemSelected');
    }
  };

  addOpenListeners() {
    if (this.closeOnEscClick) {
      document.addEventListener('keydown', this.handleKeyDown);
    }

    this.addEventListener('menu-item-selected', this.handleItemSelected);
  }

  removeOpenListeners() {
    if (this.closeOnEscClick) {
      document.removeEventListener('keydown', this.handleKeyDown);
    }

    this.removeEventListener('menu-item-selected', this.handleItemSelected);
    this.removeEventListener('mouseout', this.hide);
    this.removeEventListener('blur', this.hide);
  }

  handleAutoClose(event: KeyboardEvent | MouseEvent) {
    this.emit('popover-autoclose', {
      detail: event,
    }) as PopoverAutoCloseEvent;
  }

  handleKeyDown(event: KeyboardEvent) {
    // eslint-disable-next-line no-console
    console.log('handleKeyDown', this.active, event.key, this.closeOnEscClick);
    // Close when escape is pressed inside an open dropdown.
    if (this.active && event.key === 'Escape' && this.closeOnEscClick) {
      // if (this.contains(document.activeElement)) {
      event.stopPropagation();
      this.hide(event, 'handleKeyDown');
      this.handleAutoClose(event);
      this.isFocused = false;
      // }
    }
  }

  handleClickOutside(e: MouseEvent) {
    if (!this.active) return;

    this.handleAutoClose(e);

    const activeElId = popoverItemsSignal.value.at(-1);

    // PRE-EXISTING DEFECT, left alone here and tracked separately: the stack
    // holds `popoverId`, while this compares `this.id`. They agree only when
    // the consumer supplied an `id` attribute, or when a `trigger` RESOLVED —
    // `handleTriggerChange` is the one place in THIS class that writes
    // `popoverId` back into `id` (`PopoverTooltipWC.connectedCallback` reaches
    // the same end from the other side, giving itself an `id` that
    // `firstUpdated` then adopts as `popoverId`). A popover mounted with
    // neither keeps the `popover-<nanoid>` fallback `firstUpdated` assigns,
    // against an empty `id`, so it never matches `activeElId` and a click
    // outside can never close it. Not fixed in PSTUDIO-4229: the change flips
    // behaviour for every such popover in both apps at once and wants its own
    // ticket.
    if (this.closeOnClickOutside && this.id === activeElId) {
      this.hide(e, 'handleClickOutside');
    }
  }

  handleProjectContent(project: boolean) {
    if (project) {
      this.emit('update-portal-content', {
        detail: {
          destination: this.popoverId,
          content: this.projectedContent,
        },
      });
    } else {
      // console.log('close popover', this.parentEl);
      // Close projecting popover.
      this.emit('update-portal-content', {
        detail: {
          content: '',
          destination: this.popoverId,
        },
      });
      // Append this instance back to parent element.
      if (this.parentEl && !this.parentEl.contains(this.projectedContent)) {
        this.parentEl.append(this.projectedContent);
      }
    }
  }

  hide(e?: Event, reason: string = 'unknown') {
    if (this.active) {
      this.active = false;
      this.emit('close');
      this.isFocused = false;
      this.triggerEl?.removeAttribute('data-popover-open');
    }
  }

  show() {
    this.active = true;
    this.emit('popover-open');
    this.reposition();
    this.triggerEl?.setAttribute('data-popover-open', '');
  }

  async updated(changedProps: Map<string, unknown>) {
    super.updated(changedProps);

    // Update the anchor when trigger changes
    if (changedProps.has('trigger')) {
      await this.handleTriggerChange();
    }

    if (this.trigger && !this.triggerEl) {
      if (this.trigger && typeof this.trigger === 'string') {
        this.triggerEl = document.getElementById(this.trigger);
      }
    }

    if (this.disabled && changedProps.has('disabled')) {
      this.hide();
      this.disconnectedCallback();
    }

    // Update the anchor when trigger changes
    // Start or stop the positioner when active changes
    if (changedProps.has('active')) {
      if (this.active) {
        if (!this.omitPortal && !this.keepMounted) {
          this.handleProjectContent(true);
        }

        if (!this.disableFocusTrap) {
          // `active` toggled false → true inside one Lit batch reaches this branch
          // again without passing the close branch, so a still-pending
          // activation is replaced here instead of being left behind.
          clearTimeout(this.focusTrapActivationTimer);
          this.focusTrapActivationTimer = setTimeout(() => {
            this.focusTrapActivationTimer = undefined;
            // A host removed from the document while `active` is still true
            // (a consumer unmounting an open popover) never reaches the
            // `true → false` branch below, so the clear there cannot cancel
            // this; the check does.
            if (!this.isConnected) return;
            this.focusTrapController.activate();
          }, 10);
        }

        this.start();
        this.addOpenListeners();
        // RAISE, not push-if-absent: drop any earlier copy of this id and put
        // it back on top. Two separate failures, one write.
        //
        // A plain push duplicates: the validation tooltip took two
        // `popover-open` events against one `close`, so its id needed two
        // closes to leave the stack and the leftover copy made every `at(-1)`
        // read answer for a popover that was no longer open (PSTUDIO-4229).
        //
        // Skipping the push instead leaves an already-listed popover BURIED,
        // and `handleClickOutside` below closes only the popover that is
        // `at(-1)` — so a popover re-opened while its stale entry sat under
        // another id could never be dismissed by a click outside again.
        popoverItemsSignal.value = [
          ...popoverItemsSignal.value.filter((id) => id !== this.popoverId),
          this.popoverId,
        ];

        if (this.triggerEl?.parentNode) {
          this.mutationObserver = new MutationObserver(
            this.handleTriggerMutation
          );
          this.mutationObserver.observe(this.triggerEl.parentNode, {
            childList: true,
            subtree: true,
          });
        }
      } else if (changedProps.get('active') === true) {
        // Gated on a REAL `true → false` transition, not merely on `'active'`
        // being in the map. `packages/types/tsconfig.json` sets
        // `useDefineForClassFields: false`, so the `active = false` initializer
        // goes through Lit's accessor and puts `'active'` in
        // `changedProperties` on the FIRST update of every popover — including
        // ones nothing ever opened. Before PSTUDIO-4229 the body was behind a
        // stack check that a never-opened popover failed; now that it is
        // unconditional, that first update would run the whole close path:
        // `handleProjectContent(false)` emits `update-portal-content` on
        // `document`, which every live `ps-portal-destination` handles in the
        // CAPTURE phase (`portal-destination.wc.ts`) — dozens of them in a
        // workflows table — and two popovers sharing a `popoverId` would have
        // the second one's mount evict the first one's parked node with
        // `replaceChildren('')`, the same `NotFoundError` this ticket is about.
        //
        // Stack position still plays no part, so the PSTUDIO-4229 fix is intact:
        // what this restores is "did it close", not "was it on top".
        //
        // Deactivated unconditionally, like everything else in this branch.
        //
        // The stack gate this replaces read as a guard against a delayed focus
        // return stealing focus from a popover above. It could not be one, for
        // two independent reasons. (a) For the ordinary portalled popover it
        // decided nothing: `handleProjectContent(false)` three lines below
        // emits `update-portal-content` with empty content,
        // `PortalDestinationWC.updatePortalContent` handles it on `document`
        // in the CAPTURE phase and calls `slotElement.replaceChildren('')`
        // synchronously, and because that element's `createRenderRoot()`
        // returns `this` the popover host really does leave the document —
        // which runs `disconnectedCallback` below, whose
        // `focusTrapController?.hostDisconnected()` deactivates the trap
        // anyway. (b) The return is already declined at the source:
        // `FocusTrapController.setReturnFocus` asks `hasFocusMovedAway()` on
        // BOTH of the calls focus-trap makes per deactivation, and that walks
        // the `activeElement` chain through shadow roots — a popover above
        // holding focus is a foreign owner, so the return is cancelled there
        // (`internal/focus-trap.ts`, pinned by its own suite's "a deliberate
        // focus move outside the trap wins").
        //
        // What the gate DID change is the `omitPortal` / `keepMounted`
        // popover, which never disconnects: there a non-top popover kept a
        // live trap after closing, i.e. an invisible element still confining
        // Tab. That is the behaviour this removes.
        this.focusTrapController.deactivate();
        // `deactivate()` above does nothing while the trap is still inactive, so
        // an activation scheduled by the open branch has to be cancelled here or
        // it arms a trap on a popover that is already closed.
        clearTimeout(this.focusTrapActivationTimer);
        this.focusTrapActivationTimer = undefined;

        // The marker {@link show} writes, cleared on every close and not only
        // on the ones that go through {@link hide}. A consumer that drives
        // `active` as a prop (`row-context-menu.tsx`) never calls `hide()`, so
        // without this the marker STICKS — and it is visible: `button.scss`,
        // `icon-button.scss` and Pro Studio's `draggable-row.scss` all paint
        // from it, which is the purple row commit 268b3abc4d had to chase.
        // `PopoverTooltipWC` used to carry its own copy of exactly this line;
        // it lives here now so every subclass and every consumer gets it.
        this.triggerEl?.removeAttribute('data-popover-open');

        // Everything below is the exact undo of what the `active` branch above
        // does UNCONDITIONALLY, so it has to run unconditionally too. The
        // asymmetry — projected always, returned only when top of the stack —
        // is the defect: with a second popover open (a cell's validation
        // tooltip alongside its context menu) the closing popover left its own
        // node parked in the portal, and React's next unmount of the cell threw
        // `NotFoundError: Failed to execute 'removeChild' on 'Node'`, taking the
        // whole editor into the error boundary (PSTUDIO-4229).
        if (!this.omitPortal && !this.keepMounted) {
          this.handleProjectContent(false);
        }
        this.stop();
        this.removeOpenListeners();
        this.mutationObserver?.disconnect();
        popoverItemsSignal.value = popoverItemsSignal.value.filter(
          (id) => id !== this.popoverId
        );
      }
    }

    // If we don't have parentElement available/connected, we should toggle popover back to disabled state.
    // This is to account route change or scenario, where parent element gets removed from html tree.
    // if (!this.parentEl?.isConnected || !this.parentEl?.checkVisibility()) {
    //   this.active = false;
    // }
  }

  @property({ type: String, reflect: true, attribute: 'popover-id' })
  popoverId: string;

  private handleTriggerMutation = (mutations: MutationRecord[]) => {
    for (const mutation of mutations) {
      if ([...mutation.removedNodes].includes(this.triggerEl as Node)) {
        this.focusTrapController?.hostDisconnected();
        this.hide(undefined, 'handleTriggerMutation');

        this.stop();
        this.removeOpenListeners();
      }
    }
  };

  onClick() {
    if (!this.active) {
      this.show();
    } else {
      if (this.isFocused) return;
      this.hide();
    }
  }

  onFocusOut() {
    this.isFocused = false;
    this.hide();
  }

  onFocusIn() {
    this.isFocused = true;
    this.show();
  }

  onContextMenu(e: Event) {
    e.preventDefault();
    e.stopImmediatePropagation();

    if (!this.active) {
      this.show();
    } else {
      if (this.isFocused) return;
      this.hide();
    }
  }

  onKeyDown(e: KeyboardEvent) {
    if (e.which === 32 || e.which === 13 || e.key === 'Enter') {
      if (this.isFocused) return;

      e.preventDefault();

      if (!this.active) {
        this.show();
      } else {
        if (this.isFocused) return;
        this.hide();
      }
    }
  }

  onMouseOut() {
    if (this.isFocused) return;
    this.hide();
  }

  onMouseOver() {
    if (this.active) return;
    if (this.isFocused) return;
    this.show();
    this.triggerEl?.addEventListener('mouseout', this.onMouseOut);
  }

  private async handleTriggerChange(): Promise<void> {
    return new Promise((resolve) => {
      this.stop().then(() => {
        const previousTriggerEl = this.triggerEl;
        // temp workaround till we can tighten this up
        try {
          if (this.trigger && typeof this.trigger === 'string') {
            // Locate the anchor by id
            this.triggerEl = document.getElementById(this.trigger);

            if (!this.triggerEl) {
              this.triggerEl = (
                this.getRootNode() as ShadowRoot
              )?.getElementById(this.trigger);
            }
          } else if (this.trigger instanceof Element) {
            // Use the anchor's reference
            this.triggerEl = this.trigger;
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(
            'Invalid anchor element: no anchor could be found using the anchor slot or the anchor attribute.'
          );
        }

        // The old anchor keeps nothing of ours. `data-popover-id` outlived the
        // move (a second popover resolving to that element would be shadowed by
        // a dead id) and so did `data-popover-open`, which is a VISIBLE marker:
        // `button.scss` / `icon-button.scss` and Pro Studio's
        // `draggable-row.scss` all paint from it, so a stale one leaves a
        // control drawn as open against nothing. Only our own stamp is removed
        // — an id another popover has since written stays its own.
        if (
          previousTriggerEl &&
          previousTriggerEl !== this.triggerEl &&
          previousTriggerEl.getAttribute('data-popover-id') === this.popoverId
        ) {
          previousTriggerEl.removeAttribute('data-popover-id');
          previousTriggerEl.removeAttribute('data-popover-open');
        }

        if (!this.triggerEl) {
          // throw new Error(
          //   'Invalid anchor element: no anchor could be found using the anchor slot or the anchor attribute.'
          // );
        } else {
          if (!this.disableTriggerListeners) {
            if (
              this.triggerAction === 'click' ||
              this.triggerAction === 'click-and-hover'
            ) {
              if (this.triggerAction === 'click') {
                // automatically add click event bindings to trigger
                this.triggerEl?.addEventListener('click', this.onClick);
              } else if (this.triggerAction === 'click-and-hover') {
                this.triggerEl?.addEventListener('mouseover', this.onMouseOver);
                this.triggerEl?.addEventListener('focusin', this.onFocusIn);
                this.triggerEl?.addEventListener('focusout', this.onFocusOut);
              }

              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              this.triggerEl?.addEventListener('keydown', this.onKeyDown);

              this.onClickAway.excludeElement = this.triggerEl as HTMLElement;
            } else {
              this.triggerEl?.addEventListener(
                'contextmenu',
                this.onContextMenu
              );
            }
          }

          this.popoverId =
            this.popoverId || this.getAttribute('id') || `popover-${nanoid()}`;
          this.setAttribute('id', this.popoverId);
          this.triggerEl.setAttribute('data-popover-id', this.popoverId);
          // The instance behind that stamp. `opensANestedPopover` reads this
          // rather than the attribute, so the two are written together.
          PopoverBaseWC.popoverByTrigger.set(this.triggerEl, this);

          if (this.omitPortal) return;

          const rootElement =
            document.querySelector('ps-page') !== null
              ? (document.querySelector('ps-page') as PageWC)
              : document.body;

          const portalDestinationEl = document.querySelector(
            `${PortalDestinationWC.tagname}[name="${this.popoverId}"]`
          );

          if (!portalDestinationEl) {
            const destinationEl = document.createElement(
              PortalDestinationWC.tagname
            );

            destinationEl.setAttribute('name', this.popoverId);
            rootElement.appendChild(destinationEl);
          }
        }

        resolve();
      });
    });
  }

  private start() {
    // We can't start the positioner without an anchor
    if (!this.triggerEl) {
      return;
    }

    this.cleanup = autoUpdate(this.triggerEl, this.containerEl, () => {
      this.reposition();
    });
  }

  private async stop(): Promise<void> {
    return new Promise((resolve) => {
      if (this.cleanup) {
        this.cleanup();
        this.cleanup = undefined;
        this.removeAttribute('data-current-placement');
        requestAnimationFrame(() => resolve());
      } else {
        resolve();
      }
    });
  }

  /** Forces the popover to recalculate and reposition itself. */
  reposition() {
    // Nothing to do if the popover is inactive or the trigger doesn't exist
    if (!this.active) return;

    if (!this.triggerEl || !this.triggerEl?.isConnected) {
      // Tracked, and cleared on disconnect: a close that outlives its element
      // runs `emit('close')` against a document that may no longer exist. In a
      // test run that lands after teardown and surfaces as an unhandled
      // `dispatchEvent` TypeError, which fails the run with every test green.
      // Re-scheduling also replaces the previous timer instead of stacking one
      // per reposition.
      clearTimeout(this.noTriggerCloseTimer);
      this.noTriggerCloseTimer = setTimeout(() => {
        this.noTriggerCloseTimer = undefined;
        if (!this.triggerEl || !this.triggerEl?.isConnected) {
          this.hide(undefined, 'no trigger');
        }
      }, 100);

      return;
    }

    //
    // NOTE: Floating UI middlewares are order dependent: https://floating-ui.com/docs/middleware
    //
    const middleware = [
      // The offset middleware goes first
      offset({ mainAxis: this.distance, crossAxis: this.skidding }),
    ];

    if (this.flip) {
      middleware.push(
        flip({
          fallbackAxisSideDirection: 'start',
          crossAxis: !this.shift,
        })
      );
    }

    if (this.shift) {
      middleware.push(
        shift({
          padding: 16,
        })
      );
    }

    if (this.hasArrow) {
      middleware.push(
        arrow({
          element: this.arrowEl,
          padding: this.arrowPadding,
        })
      );
    }

    computePosition(this.triggerEl, this.containerEl, {
      placement: this.placement,
      middleware,
      strategy: 'fixed',
    }).then(({ x, y, middlewareData, placement }) => {
      this.setAttribute('data-current-placement', placement);

      this.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;

      if (this.hasArrow && middlewareData.arrow) {
        const { x: arrowX, y: arrowY } = middlewareData.arrow;

        const staticSide: string = {
          top: 'bottom',
          right: 'left',
          bottom: 'top',
          left: 'right',
        }[placement.split('-')[0]] as string;

        Object.assign(this.arrowEl.style, {
          left: arrowX != null ? `${arrowX}px` : '',
          top: arrowY != null ? `${arrowY}px` : '',
          right: '',
          bottom: '',
          [staticSide]: '-4px',
        });
      }
    });
  }
}
